import Tooltip from '@mui/material/Tooltip';
import React, { FunctionComponent } from 'react';
import { useNavigate } from 'react-router-dom';
import { BooleanParam, useQueryParam } from 'use-query-params';

import { MainButton } from 'components/atoms/Buttons/MainButton';
import { TableEditingContextProvider } from 'components/atoms/EditableDataGrid/TableEditingContext';
import { RowSelectionContextProvider } from 'components/atoms/RowSelectionContext';
import { Permission } from 'interfaces/permissionOption';
import { useProceduresFieldsContext } from 'interfaces/procedure/fields/helpers';
import { useSnackbar } from 'notistack';
import { CompressedJsonParam } from 'utils/customQueryParamsConfigs';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { useEncodedFilters } from 'utils/useEncodedFilters';
import { usePermissions } from 'utils/usePermissions';
import { getDatasetMode } from '../ProceduresPage/datasetMode';
import { useStudyPendingSlidesCount } from '../ProceduresPage/usePendingSlides';
import StudyHeaderActionBadge from '../StudyHeaderActionBadge';
import CaseManifestDialog from './CaseManifestDialog';
import { ManifestDialogUrlState } from './dialogState';
import { buildOrganizationManifestState, getOrganizationReturnUrl } from './organizationEntry';
import { OrganizationEntryOrigin } from './organizationTypes';

interface StudyManifestControlProps {
  studyId: string;
  showStudyManifestControl?: boolean;
}

const StudyManifestControl: FunctionComponent<React.PropsWithChildren<StudyManifestControlProps>> = ({
  studyId,
  showStudyManifestControl,
}) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { labId } = useCurrentLabId();
  const { hasPermission } = usePermissions();
  const { queryParams } = useEncodedFilters();
  const canLoadPendingSlides = Boolean(studyId && showStudyManifestControl);
  const { totalPendingSlides, isLoading, isRefreshing, isError } = useStudyPendingSlidesCount({
    studyId,
    enabled: canLoadPendingSlides,
  });

  const [isManifestDialogOpen, setIsManifestDialogOpen] = useQueryParam('isManifestDialogOpen', BooleanParam);
  const [slidesMode, setSlidesMode] = useQueryParam('slidesMode', BooleanParam);
  const [pendingSlidesMode] = useQueryParam('pendingSlidesMode', BooleanParam);
  const [manifestDialogUrlState, setManifestDialogUrlState] = useQueryParam<ManifestDialogUrlState | undefined>(
    'caseManifestState',
    CompressedJsonParam
  );
  const previousSlidesMode = slidesMode;
  const procedureFieldContext = useProceduresFieldsContext({ isEditPreview: true });

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    setManifestDialogUrlState(
      buildOrganizationManifestState({
        mode: 'standard',
        returnTo: {
          origin: 'dataset',
          labId,
          studyId,
          datasetMode: getDatasetMode({ slidesMode, pendingSlidesMode }),
          page: queryParams.page,
          searchTerm: queryParams.searchTerm || undefined,
        },
      }),
      'replaceIn'
    );
    setIsManifestDialogOpen(true);
    setSlidesMode(true);
    e.stopPropagation();
  };

  const handleClose = (e?: React.SyntheticEvent) => {
    e?.stopPropagation();
    if (manifestDialogUrlState?.organizationEntry) {
      const accessibleOrigins: OrganizationEntryOrigin[] = [
        'dataset',
        'uploads',
        ...(hasPermission(Permission.ViewPendingSlides) ? (['lostAndFound'] as const) : []),
      ];
      const returnNavigation = getOrganizationReturnUrl({
        entry: manifestDialogUrlState.organizationEntry,
        fallbackLabId: labId,
        fallbackStudyId: studyId,
        accessibleOrigins,
      });

      navigate(returnNavigation.url);
      if (returnNavigation.usedFallback) {
        enqueueSnackbar('The original page is no longer available. Returned to the study Dataset.', {
          variant: 'info',
        });
      }
      return;
    }
    setIsManifestDialogOpen(false);
    setSlidesMode(previousSlidesMode);
  };

  const countDescription = `${totalPendingSlides} ${
    totalPendingSlides === 1 ? 'slide is' : 'slides are'
  } not assigned to cases in this study.`;
  const countTooltipText = isLoading
    ? 'Loading the number of slides not assigned to cases.'
    : isError
    ? 'The number of slides not assigned to cases could not be loaded.'
    : countDescription;

  return (
    <>
      <StudyHeaderActionBadge
        color={isLoading || isRefreshing ? 'secondary' : 'primary'}
        count={totalPendingSlides}
        countAriaLabel={`${totalPendingSlides} slides not assigned to cases`}
        countTooltip={countTooltipText}
        isLoading={isLoading || isRefreshing}
        loadingAriaLabel="Loading slides not assigned to cases count"
      >
        <Tooltip title="Assign slides to cases." placement="top" arrow describeChild>
          <span>
            <MainButton
              disabled={!studyId || !showStudyManifestControl || isLoading}
              color="secondary"
              variant="contained"
              onClick={handleClick}
            >
              Organize slides
            </MainButton>
          </span>
        </Tooltip>
      </StudyHeaderActionBadge>
      <RowSelectionContextProvider urlParamPrefix="caseManifest">
        <TableEditingContextProvider fieldsContext={procedureFieldContext}>
          {/* Remount on destination changes to clear local review/retry state; the URL-backed draft is preserved. */}
          {isManifestDialogOpen ? (
            <CaseManifestDialog key={`${labId}:${studyId}`} open onClose={handleClose} studyId={studyId} />
          ) : null}
        </TableEditingContextProvider>
      </RowSelectionContextProvider>
    </>
  );
};

export default StudyManifestControl;
